import { onBeforeMount, onMounted, toRaw } from 'vue'
import { useI18n } from 'vue-i18n'
import router from '@/router'
import useNavigation from '@/common/composables/useNavigation'
import { type getDictionaryPayload } from '@/common/types/event'

import { useVonageStore } from '@/admin/stores/vonageStore'
import { useApplicationStore } from '@/common/stores/applicationStore'

import { subscribe, unsubscribe, publish } from '@/common/services/eventBusService'
import localizationServerData from '@/common/services/serverData/localizationServerData'
import type { InternalRedirectionLocation } from '@/common/types/navigation'
import { useCtiStore } from '@/cti/stores/ctiStore'
import { useBreaksStore } from '@/agent/stores/breakStore'

export function useGlobalEvents() {
  const applicationStore = useApplicationStore()
  const navigation = useNavigation()
  const i18n = useI18n()

  const getDictionary = async ({ prefixes, cb }: getDictionaryPayload) => {
    await localizationServerData.fetch({
      prefixes,
    })

    cb(toRaw(i18n.getLocaleMessage(i18n.locale.value)))
  }

  const showTicketHandler = async (caseId: string) => {
    publish('global:case-creation-cancel')
    navigation.openCase(caseId, { force: true })
  }

  const showCustomerHandler = (customerId: string) => {
    publish('global:case-creation-cancel')
    navigation.openCustomer(customerId, { force: true })
  }

  const disableNavigationHandler = () => {
    console.log('EMIT: back:disable-navigation')
    applicationStore.disabled = true
  }

  const enableNavigationHandler = () => {
    console.log('EMIT: back:enable-navigation')
    applicationStore.disabled = false
  }

  const redirectHandler = (location: InternalRedirectionLocation) => {
    if (typeof location === 'string') {
      router.push({ path: `/assist${location}`, force: true })
      return
    }

    if (typeof location === 'object' && location.url) {
      const { url, query } = location
      router.push({ path: `/assist${url}`, query, force: true })
    }
  }

  const loadWhatsappVonageNumbersHandler = () => {
    useVonageStore().passWhatsAppNumbers()
  }

  const toggleActivateWhatsappAccoundHandler = (id: number) => {
    useVonageStore().updateWhatsAppNumberActiveValue(id)
  }

  const stopBreak = () => {
    useBreaksStore().isOnBreak = false
    useCtiStore().setAgentStatus('no-break')
  }

  const bindGlobalEvents = () => {
    subscribe('back:show-ticket', showTicketHandler)
    subscribe('back:show-customer', showCustomerHandler)
    subscribe('back:disable-navigation', disableNavigationHandler)
    subscribe('back:enable-navigation', enableNavigationHandler)
    subscribe('back:redirect', redirectHandler)
    subscribe('back:load-whatsapp-vonage-numbers', loadWhatsappVonageNumbersHandler)
    subscribe('back:toggle-activate-whatsapp-account', toggleActivateWhatsappAccoundHandler)
    subscribe('front-legacy:get-dictionary', getDictionary)
    subscribe('back:stop-break', stopBreak)
  }

  const unbindGlobalEvents = () => {
    unsubscribe('back:show-ticket', showTicketHandler)
    unsubscribe('back:show-customer', showCustomerHandler)
    unsubscribe('back:disable-navigation', disableNavigationHandler)
    unsubscribe('back:enable-navigation', enableNavigationHandler)
    unsubscribe('back:redirect', redirectHandler)
    unsubscribe('back:load-whatsapp-vonage-numbers', loadWhatsappVonageNumbersHandler)
    unsubscribe('back:toggle-activate-whatsapp-account', toggleActivateWhatsappAccoundHandler)
    unsubscribe('front-legacy:get-dictionary', getDictionary)
  }

  onMounted(() => {
    bindGlobalEvents()
  })

  onBeforeMount(() => {
    unbindGlobalEvents()
  })
}
